import cx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';
import { ReactComponent as BlockIcon } from '@assets/svg/users/ic-block-user.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Label from '@components/uikit/Label/Label';

import { initialSorting } from '@constants/tableSorting';

import { getUserAccessAndRole } from '@helpers/getUserAccessAndRole';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useUsersInfiniteQuery } from '../../services/useUsersInfiniteQuery';
import {
  selectUsersSearchValue,
  selectUsersStatusValue,
  selectUsersTableSortingValue,
  useUsersActions,
} from '../../store/useUsersStore';
import ContextMenu from '../ContextMenu/ContextMenu';
import TableUsersSkeleton from './TableUsersSkeleton';
import style from './style.module.scss';

const TableUsers = () => {
  const { t } = useTranslation();
  const [totalByStatus, setTotalByStatus] = useState({
    all: 0,
    active: 0,
    inactive: 0,
    unconfirmed: 0,
  });
  const usersTableContainerRef = useRef(null);

  const { setUsersTableSortingValue } = useUsersActions();

  const searchValue = selectUsersSearchValue();
  const userStatus = selectUsersStatusValue();
  const sorting = selectUsersTableSortingValue();
  const locationSelectValue = selectAppLocationSelectValue();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const { data, fetchNextPage, isFetching, hasNextPage, isLoading, isRefetching } = useUsersInfiniteQuery({
    sorting,
    searchValue: debouncedValue,
    userStatus,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { data: profileData } = useUserProfileQuery();

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, hasNextPage, isFetching]);

  useEffect(() => {
    if (data && userStatus) {
      usersTableContainerRef.current.scroll({ top: 0, behavior: 'smooth' });
    }
  }, [userStatus]);

  const users = useMemo(() => {
    if (!data) {
      return [];
    }
    const total = data?.pages[data.pages.length - 1]?.data?.totalUsersCount;
    setTotalByStatus({ ...totalByStatus, [userStatus]: total });

    return data?.pages?.flatMap((page) => page.data.users) ?? [];
  }, [data]);

  const onChangeSorting = (key) => {
    if (!sorting.id || sorting.id !== key) {
      setUsersTableSortingValue({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setUsersTableSortingValue(initialSorting);
    } else {
      setUsersTableSortingValue({ id, desc: true });
    }
  };

  const tableLoading = isLoading || isRefetching;

  const userStatuses = {
    active: 'success',
    inactive: 'critical',
    unconfirmed: 'high',
  };

  const getUpdatedAt = (date) => {
    if (!date) {
      return t('Never');
    }

    return formatDistanceToNow(fromZonedTime(date, userTimezone), {
      addSuffix: true,
    });
  };

  return (
    <div>
      <div className={style.usersTableWrapper} ref={usersTableContainerRef}>
        <table className={style.usersTable}>
          <thead>
            <tr>
              <th className={style.nameColumn}>
                {t('Name')}
                <TableSort
                  className={cx(style.usersTableSortIcon, { [style.activeSort]: sorting.id === 'fullName' })}
                  onClick={() => onChangeSorting('fullName')}
                  data-tooltip-content="fullName"
                  data-tooltip-id="sort-tooltip"
                />
              </th>
              <th className={style.emailColumn}>
                {t('Email')}
                <TableSort
                  className={cx(style.usersTableSortIcon, { [style.activeSort]: sorting.id === 'email' })}
                  onClick={() => onChangeSorting('email')}
                  data-tooltip-content="email"
                  data-tooltip-id="sort-tooltip"
                />
              </th>
              <th className={style.usernameColumn}>
                {t('User Name')}
                <TableSort
                  className={cx(style.usersTableSortIcon, { [style.activeSort]: sorting.id === 'username' })}
                  onClick={() => onChangeSorting('username')}
                  data-tooltip-content="username"
                  data-tooltip-id="sort-tooltip"
                />
              </th>
              <th>{t('Role')}</th>
              <th>
                <div className={style.usersTableHeadCellContainer}>
                  <span className={style.flexHeader}>{t('Equipment Access')}</span>
                </div>
              </th>
              <th>
                <div className={style.userStatusColumn}>
                  <div>
                    <div>{t('Status')} / </div>
                    <div>{t('Latest Active Session')}</div>
                  </div>
                  <TableSort
                    className={style.usersTableSortIcon}
                    onClick={() => onChangeSorting('lastSeen')}
                    data-tooltip-content="lastSeen"
                    data-tooltip-id="sort-tooltip"
                  />
                </div>
              </th>
              {profileData?.userAccess?.manageUsers && <th className={style.actionsColumn} />}
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <TableUsersSkeleton />
            ) : users.length ? (
              users.map((row) => (
                <tr key={row.id} className={style.usersBodyRow}>
                  <td className={style.nameColumn}>
                    {row.fullName}
                    <div
                      className={cx(style.emailHidden, style.ellipsisTextOneLine)}
                      data-tooltip-content={row.email}
                      data-tooltip-id="cell-tooltip"
                    >
                      {row.email}
                    </div>
                  </td>
                  <td className={style.emailColumn}>
                    <div
                      className={style.ellipsisTextOneLine}
                      data-tooltip-content={row.email}
                      data-tooltip-id="cell-tooltip"
                    >
                      {row.email}
                    </div>
                  </td>
                  <td className={style.usernameColumn}>
                    <div
                      className={style.ellipsisTextOneLine}
                      data-tooltip-content={row.username}
                      data-tooltip-id="cell-tooltip"
                    >
                      {row.username}
                    </div>
                  </td>
                  <td>{t(getUserAccessAndRole(row.permissions).userRole)}</td>
                  <td>
                    {row.allLocations
                      ? t('All Locations')
                      : row.facilities.map((loc) => <div key={loc}>{t(loc)}</div>)}
                  </td>
                  <td>
                    <div className={style.userStatusWrapper}>
                      {row.is_blocked && <BlockIcon className={style.blockIcon} />}
                      <Label text={t(row.status)} variant={userStatuses[row.status]} />
                    </div>
                    <div className={style.lastSeen}>{getUpdatedAt(row.lastSeen)}</div>
                  </td>
                  {profileData?.userAccess?.manageUsers && (
                    <td className={style.actionsColumn}>
                      <div
                        className={style.actionsIconContainer}
                        data-tooltip-id="table-users-action-menu"
                        data-tooltip-content={JSON.stringify(row)}
                      >
                        <ThreeDotsIcon />
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <TableEmptyRow text={t('No users were found.')} />
            )}
          </tbody>
          <tfoot>
            <TableLoadingTrigger show={!isFetching} ref={ref} />
            <TableLoading show={isFetching && !isLoading} />
          </tfoot>
        </table>
        <TooltipSort id="sort-tooltip" sorting={sorting} />
        <Tooltip id="cell-tooltip" place="top-start" />
        <ContextMenu id="table-users-action-menu" />
      </div>
      <div className={style.usersFooter}>
        <span className={style.usersTotalLength}>
          {`${t('Displaying')} ${tableLoading ? 0 : users.length} ${t('of')} ${
            tableLoading ? 0 : totalByStatus[userStatus]
          } ${t('Users')}`}
        </span>
        <ButtonGoUp ref={usersTableContainerRef} />
      </div>
    </div>
  );
};

export default TableUsers;
